import { useEffect, useRef } from 'react';
import { GameSurface } from '@/components/GameSurface';
import type { Game } from '@/types/game';
import type { ChallengeWithProgress } from '@/types/game';

export interface PreviewCarouselProps {
  games: Game[];
  todayChallenges: Record<string, ChallengeWithProgress>;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onCTAClick?: () => void;
  autoAdvanceMs?: number;
}

// IS-INV-009: All colors via design tokens — no hardcoded hex or pixel values in carousel chrome.
// IS-INV-035: Mobile tap targets ≥ 44px enforced via min-h-[44px] / min-w-[44px] on interactive elements.
// IS-INV-040: GameSurface owns URL construction — PreviewCarousel never constructs iframe src directly.
export function PreviewCarousel({ games, todayChallenges, activeIndex, onActiveIndexChange, onCTAClick, autoAdvanceMs = 10000 }: PreviewCarouselProps) {
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gamesRef = useRef(games);

  useEffect(() => { gamesRef.current = games; }, [games]);

  useEffect(() => {
    if (gamesRef.current.length <= 1) return;

    const duration = gamesRef.current[activeIndex]?.previewAnimationMs ?? autoAdvanceMs;
    intervalRef.current = setTimeout(() => {
      onActiveIndexChange((activeIndex + 1) % gamesRef.current.length);
    }, duration);

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
    // gamesRef intentional: avoids timer reset on parent re-renders with new array reference
  }, [activeIndex, autoAdvanceMs, onActiveIndexChange]);

  if (games.length === 0) return null;

  const activeGame = games[activeIndex];
  const todayChallenge = activeGame ? todayChallenges[activeGame.id] : undefined;
  const canPlay = todayChallenge?.accessControl?.canPlay === true;

  return (
    <div className="relative overflow-hidden rounded-xl bg-surface">
      {/* Slide */}
      <div className="flex flex-col md:flex-row h-auto md:h-[220px]">
        {/* Right panel (visual) — stacks on top at mobile (IS-INV-035 mobile stacking) */}
        <div className="order-1 md:order-2 flex-1 min-h-[160px] md:min-h-[220px]">
          {activeGame?.frontendUrl ? (
            <GameSurface
              game={activeGame}
              surfaceType="preview"
              className="h-[160px] md:h-[220px] w-full rounded-none"
            />
          ) : (
            <div className="h-[160px] md:h-[220px] w-full bg-surface-alt" />
          )}
        </div>

        {/* Left panel (info) — 180px fixed on desktop, full-width below on mobile */}
        <div className="order-2 md:order-1 flex flex-col justify-center gap-2 p-3 md:w-[180px] md:flex-shrink-0">
          {/* Icon badge with signature color accent */}
          {activeGame && (
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: activeGame.signatureColor }}
              >
                {activeGame.iconUrl ? (
                  <img
                    src={activeGame.iconUrl}
                    alt={activeGame.displayName}
                    className="h-5 w-5 object-contain"
                  />
                ) : (
                  <span className="font-display text-sm text-primary-foreground">
                    {activeGame.displayName.slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>

              <span className="font-emphasis text-sm font-semibold uppercase tracking-wide text-foreground">
                {activeGame.displayName}
              </span>
            </div>
          )}

          {/* Tagline — omitted gracefully if absent */}
          {activeGame?.previewTagline && (
            <p className="font-body text-xs text-muted-foreground leading-snug">
              {activeGame.previewTagline}
            </p>
          )}

          {/* CTA — only rendered when today's challenge is playable; navigation is Games.tsx's responsibility */}
          {canPlay && onCTAClick && (
            <button
              onClick={onCTAClick}
              // iOS Safari overrides text-primary-foreground class; inline fill forces token value
              style={{ WebkitTextFillColor: 'var(--primary-foreground)' }}
              className="font-emphasis min-h-[44px] w-full rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Play today's puzzle →
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {games.length > 1 && (
        <div className="h-[3px] w-full bg-border">
          <div
            key={activeIndex}
            className="h-full bg-primary"
            style={{
              animation: `progress-fill ${games[activeIndex]?.previewAnimationMs ?? autoAdvanceMs}ms linear forwards`,
            }}
          />
        </div>
      )}

    </div>
  );
}
